import { H2, H3 } from '@amzn/stencil-react-components/text';

import { truncateStyle } from './truncateStyle';

interface TruncatedTitleProps {
  children: React.ReactNode;
  /**
   * Maximum number of lines before truncation with ellipsis.
   * Defaults to 1 (single-line truncation).
   */
  maxLines?: number;
  /**
   * Heading level to render. Defaults to 3 (card + most page titles).
   * Detail-page titles use 2 to match the larger heading used across
   * audit/task detail headers.
   */
  level?: 2 | 3;
}

/**
 * Truncating heading for card and page titles.
 *
 * - `maxLines={1}` (default): single-line with text-overflow ellipsis
 * - `maxLines={2+}`: multi-line clamp with -webkit-line-clamp
 * - `level={2}`: render as H2 (larger detail-page title); defaults to H3
 *
 * The full text is accessible via the native `title` tooltip on hover.
 *
 * @example
 * ```tsx
 * <TruncatedTitle>{audit.name}</TruncatedTitle>
 * <TruncatedTitle level={2} maxLines={2}>{task.name}</TruncatedTitle>
 * ```
 */
export function TruncatedTitle({
  children,
  maxLines = 1,
  level = 3,
}: TruncatedTitleProps): JSX.Element {
  const Heading = level === 2 ? H2 : H3;
  return (
    <Heading
      css={truncateStyle(maxLines)}
      title={typeof children === 'string' ? children : undefined}
    >
      {children}
    </Heading>
  );
}
